export type UiEvent = {
  type: string;
  label?: string;
  detail?: string;
  data?: Record<string, unknown>;
  at?: string;
};

export type RealtimeSignal =
  | "session_ready"
  | "user_speaking"
  | "user_stopped"
  | "response_started"
  | "assistant_speaking"
  | "response_finished"
  | "response_cancelled"
  | "output_cleared"
  | "error";

export type MappedRealtimeEvent = {
  signal?: RealtimeSignal;
  uiEvent?: UiEvent;
};

export function openingResponseEvent(greeting: string) {
  return {
    type: "response.create",
    response: {
      instructions: `For this opening response, say exactly the following and nothing else: ${greeting} Stop immediately after it. Do not add another message, example, suggestion, capability, or question.`,
    },
  };
}

export function callerSpeechPresentation(assistantSpeaking: boolean, agentName = "the front desk") {
  return assistantSpeaking
    ? { mode: "interrupted" as const, status: "INTERRUPTED / LISTENING", detail: `${agentName} paused; keep speaking` }
    : { mode: "listening" as const, status: "LIVE / LISTENING", detail: "Listening until you finish your thought" };
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" ? value as Record<string, unknown> : null;
}

function text(value: unknown) {
  return typeof value === "string" ? value : "";
}

export function mapRealtimeEvent(raw: unknown, agentLabel = "AI Front Desk"): MappedRealtimeEvent[] {
  const event = record(raw);
  if (!event) return [];
  const type = text(event.type);
  const key = text(event.item_id) || text(event.response_id) || text(event.event_id) || type;

  switch (type) {
    case "session.created":
    case "session.updated":
      return [{ signal: "session_ready" }];
    case "input_audio_buffer.speech_started":
      return [{ signal: "user_speaking", uiEvent: { type: "transcript.user", label: "You", detail: "Listening…", data: { key: `user:${key}`, final: false, placeholder: true } } }];
    case "input_audio_buffer.speech_stopped":
      return [{ signal: "user_stopped" }];
    case "conversation.item.input_audio_transcription.completed": {
      const transcript = text(event.transcript).trim();
      return [{ uiEvent: { type: "transcript.user", label: "You", detail: transcript, data: { key: `user:${key}`, final: true, remove: !transcript } } }];
    }
    case "conversation.item.input_audio_transcription.failed": {
      const error = record(event.error);
      return [
        { uiEvent: { type: "transcript.user", label: "You", detail: "", data: { key: `user:${key}`, remove: true } } },
        { signal: "error", uiEvent: { type: "realtime.error", label: "Transcription error", detail: text(error?.message) || "Caller transcription failed" } },
      ];
    }
    case "response.created":
      return [{ signal: "response_started" }];
    case "response.output_audio.delta":
      return [{ signal: "assistant_speaking" }];
    case "response.output_audio_transcript.delta": {
      const delta = text(event.delta);
      return delta ? [{ signal: "assistant_speaking" }, { uiEvent: { type: "transcript.agent", label: agentLabel, detail: delta, data: { key: `agent:${key}`, append: true, final: false } } }] : [{ signal: "assistant_speaking" }];
    }
    case "response.output_audio_transcript.done": {
      const transcript = text(event.transcript).trim();
      return transcript ? [{ uiEvent: { type: "transcript.agent", label: agentLabel, detail: transcript, data: { key: `agent:${key}`, final: true } } }] : [];
    }
    case "response.cancelled":
      return [{ signal: "response_cancelled" }];
    case "output_audio_buffer.cleared":
      return [{ signal: "output_cleared" }];
    case "response.done": {
      const response = record(event.response);
      const status = text(response?.status);
      return [{ signal: status === "cancelled" ? "response_cancelled" : "response_finished" }];
    }
    case "error": {
      const error = record(event.error);
      return [{ signal: "error", uiEvent: { type: "realtime.error", label: "Realtime error", detail: text(error?.message) || "Realtime connection error" } }];
    }
    default:
      return [];
  }
}
